import { RedeemCode, RedeemCodeResponse } from 'mytours-read-api-typescript'

import { getConfiguration } from '../configuration/index'
import { CouponCode } from './types'

interface CodeTarget {
	couponCode?: string
	linkedId?: number
	linkedType?: RedeemCode.LinkedTypeEnum
}

/**
 * Whether a validation never reached the server, as opposed to being answered by it.
 *
 * The generated client throws the `Response` for any non-2xx, so a rejection carrying a numeric
 * `status` is the server's verdict and must be honoured — that is how a revoked or expired code loses
 * access. A rejection without one is a `TypeError` from fetch: no connection, DNS, or a blocked
 * preflight. Nothing was decided, so nothing should be concluded from it.
 */
export const serverUnreachable = (error: unknown): boolean =>
	typeof (error as { status?: unknown })?.status !== 'number'

/** Whether this device already holds this exact code for this exact target. */
export const heldOnDevice = async ({ couponCode, linkedId, linkedType }: CodeTarget): Promise<boolean> => {
	if (!couponCode || linkedId === undefined || linkedType === undefined) return false

	try {
		const projectId = getConfiguration().getDefaultAppInfo().projectId
		const stored = (await getConfiguration().loadCouponCodesFromDisk(projectId)) ?? []

		return stored.some(
			(code: CouponCode) => code?.code === couponCode && code?.linkedId === linkedId && code?.linkedType === linkedType,
		)
	} catch {
		// A disk read that will not answer is not evidence of access, so treat it as none held.
		return false
	}
}

/**
 * Keep the access a device already earned when the server cannot be asked about it.
 *
 * Boot revalidation is what makes server-side revocation stick, so it runs on every launch and its
 * verdict wins whenever there is one. Offline there is no verdict, and failing closed would lock a
 * visitor out of a tour they downloaded precisely so they could use it without a connection — the
 * museum basement being the case the feature exists for.
 *
 * So this widens nothing: it answers only for a code already recorded as redeemed on this device, and
 * only when the request could not be delivered. A code typed at the keypad has no stored redemption
 * behind it and still fails, and the next launch with a connection revokes it for real.
 */
export const withOfflineAccess =
	<P>(
		validate: (payload: P, options: RequestInit) => Promise<RedeemCodeResponse>,
		// Callers whose payload already names the target this way need no mapper.
		toTarget: (payload: P) => CodeTarget = payload => payload as CodeTarget,
	) =>
	async (payload: P, options: RequestInit): Promise<RedeemCodeResponse> => {
		try {
			return await validate(payload, options)
		} catch (error) {
			if (serverUnreachable(error) && (await heldOnDevice(toTarget(payload)))) {
				return { status: RedeemCodeResponse.StatusEnum.Valid }
			}

			throw error
		}
	}
